<template>
  <div class="coupon-property-user-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('couponPropertyUser.usageRecord')"
      :subtitle="$t('couponPropertyUser.page.subtitle')" />

    <el-row :gutter="16">
      <!-- 左侧：扫码核销 -->
      <el-col :xs="24" :md="8" :lg="6">
        <div class="ds-panel ds-panel--padded">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('couponPropertyUser.verifyResult') }}</span>
          </div>

          <el-row :gutter="8">
            <el-col :xs="24" :sm="18">
              <el-input v-model="couponId" :placeholder="$t('couponPropertyUser.scanPlaceholder')"
                @keyup.enter.native="handleConfirmCoupon" />
            </el-col>
            <el-col :xs="24" :sm="6">
              <el-button type="primary" class="w-full" @click="handleConfirmCoupon">
                {{ $t('couponPropertyUser.verify') }}
              </el-button>
            </el-col>
          </el-row>

          <div class="ds-info-grid ds-info-grid--compact">
            <div class="ds-info-grid__item">
              <span class="ds-info-grid__label">{{ $t('couponPropertyUser.verifyResult') }}</span>
              <span class="ds-info-grid__value">{{ order.remark || '-' }}</span>
            </div>
            <div class="ds-info-grid__item">
              <span class="ds-info-grid__label">{{ $t('couponPropertyUser.verifyTime') }}</span>
              <span class="ds-info-grid__value ds-num">{{ order.createTime || '-' }}</span>
            </div>
            <div class="ds-info-grid__item">
              <span class="ds-info-grid__label">{{ $t('couponPropertyUser.couponName') }}</span>
              <span class="ds-info-grid__value">{{ order.couponName || '-' }}</span>
            </div>
            <div class="ds-info-grid__item">
              <span class="ds-info-grid__label">{{ $t('couponPropertyUser.faceValue') }}</span>
              <span class="ds-info-grid__value ds-num">{{ order.value || '-' }}</span>
            </div>
            <div class="ds-info-grid__item">
              <span class="ds-info-grid__label">{{ $t('couponPropertyUser.userName') }}</span>
              <span class="ds-info-grid__value">{{ order.userName || '-' }}</span>
            </div>
            <div class="ds-info-grid__item">
              <span class="ds-info-grid__label">{{ $t('couponPropertyUser.phone') }}</span>
              <span class="ds-info-grid__value ds-num">{{ order.tel || '-' }}</span>
            </div>
            <div class="ds-info-grid__item">
              <span class="ds-info-grid__label">{{ $t('couponPropertyUser.purpose') }}</span>
              <span class="ds-info-grid__value">{{ order.toTypeName || '-' }}</span>
            </div>
          </div>
        </div>
      </el-col>

      <!-- 右侧：使用记录 -->
      <el-col :xs="24" :md="16" :lg="18">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.couponName" clearable
                :placeholder="$t('couponPropertyUser.couponNamePlaceholder')" @keyup.enter.native="handleQuery" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.userName" clearable
                :placeholder="$t('couponPropertyUser.userNamePlaceholder')" @keyup.enter.native="handleQuery" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.tel" clearable
                :placeholder="$t('couponPropertyUser.phonePlaceholder')" @keyup.enter.native="handleQuery" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleQuery">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ $t('couponPropertyUser.usageRecord') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="couponPropertyUsers" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('couponPropertyUser.page.empty')" @retry="getList">
            <el-table-column prop="uoId" :label="$t('couponPropertyUser.serialNumber')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.uoId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="couponName" :label="$t('couponPropertyUser.couponName')" align="left" min-width="160" show-overflow-tooltip />
            <el-table-column prop="value" :label="$t('couponPropertyUser.faceValue')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.value }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="userName" :label="$t('couponPropertyUser.userName')" align="center" width="120" />
            <el-table-column prop="tel" :label="$t('couponPropertyUser.phone')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.tel || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="remark" :label="$t('couponPropertyUser.purpose')" align="center" min-width="140" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.remark || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('couponPropertyUser.usageTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { listCouponPropertyUserDetail, writeOffCouponPropertyUser } from '@/api/scm/couponPropertyUserDetailApi'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CouponPropertyUserDetailList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      couponId: '',
      couponPropertyUsers: [],
      order: {
        remark: '',
        userName: '',
        createTime: '',
        tel: '',
        couponName: '',
        value: '',
        toTypeName: ''
      },
      conditions: {
        couponId: '',
        couponName: '',
        validityDay: '',
        userName: '',
        tel: '',
        toType: '',
        state: '',
        communityId: getCommunityId()
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.conditions
        }
        const { data, total } = await listCouponPropertyUserDetail(params)
        this.couponPropertyUsers = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('查询优惠券使用记录失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('couponPropertyUser.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleQuery() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.conditions.couponName = ''
      this.conditions.userName = ''
      this.conditions.tel = ''
      this.page.current = 1
      this.getList()
    },
    async handleConfirmCoupon() {
      if (!this.couponId) {
        this.$message.warning(this.$t('couponPropertyUser.scanTip'))
        return
      }

      try {
        const data = {
          couponQrcode: this.couponId,
          communityId: this.conditions.communityId,
          giftCount: 1
        }
        const res = await writeOffCouponPropertyUser(data)
        if (res.code !== 0) {
          this.$message.error(res.msg)
          return
        }

        this.couponId = ''
        this.$message.success(this.$t('common.operationSuccess'))
        this.getList()

        if (res.data && res.data.length > 0) {
          this.order = { ...res.data[0] }
          if (!this.order.remark) {
            this.order.remark = this.$t('couponPropertyUser.verifySuccess')
          }
        }
      } catch (error) {
        this.couponId = ''
        console.error('核销优惠券失败:', error)
        this.$message.error(error.message || this.$t('couponPropertyUser.verifyFailed'))
      }
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-property-user-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--ds-spacing-md) var(--ds-spacing-lg);
  margin-top: var(--ds-spacing-lg);

  &--compact {
    gap: var(--ds-spacing-sm) var(--ds-spacing-md);
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
  }

  &__value {
    font-size: var(--ds-font-size-md);
    color: var(--ds-gray-800);
    word-break: break-all;
  }
}
</style>
